import React, { useEffect } from 'react';
import clsx from 'clsx';
import { is, head } from 'ramda';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button } from '@material-ui/core';
import { calculateQuizValues } from 'utils/quizHelper';
import QuizQuestionPresenter from 'presenters/QuizQuestionPresenter';
import QuizPresenter from 'presenters/QuizPresenter';
import InputWithSlider from 'sharedComponents/InputWithSlider';

import useStyles from './useStyles';

const DTI_DEFAULT_VALUES = {
  suggested: 0.3,
  stretched: 0.36,
  aggressive: 0.43,
};

const MONTHS_IN_YEAR = 12;

const MortgagePaymentQuestion = props => {
  const { t } = useTranslation();
  const { question, onChange, values, setValidity } = props;
  const classes = useStyles();

  const dtiLevelField = head(QuizQuestionPresenter.quizFields(question));
  const currentValue = QuizPresenter[dtiLevelField](values);

  useEffect(() => {
    const isValid = is(Number, currentValue);
    setValidity(isValid);
  }, [currentValue]);

  const options = QuizQuestionPresenter.options(question);
  const { min, max, marksDivider, strictLimit } = options;
  const { mortgagePayment, incomeValue, totalDebtPayments } = calculateQuizValues(values);
  const dtiLevel = QuizPresenter.dtiLevel(values);

  const minMortgagePayment = Math.max((incomeValue * Number(min / 100)) / MONTHS_IN_YEAR - totalDebtPayments, 0);
  const maxMortgagePayment = Math.max((incomeValue * Number(max / 100)) / MONTHS_IN_YEAR - totalDebtPayments, 0);

  const handleValueChange = value => {
    const calculatedDtiLevel = (Number(12) * (value + totalDebtPayments)) / incomeValue;
    onChange({ field: 'dtiLevel', value: calculatedDtiLevel });
  };

  const handleClickDtiButton = type => () => {
    onChange({ field: 'dtiLevel', value: DTI_DEFAULT_VALUES[type] });
  };

  const isSuggested = dtiLevel <= DTI_DEFAULT_VALUES.suggested;
  const isStretched = dtiLevel <= DTI_DEFAULT_VALUES.stretched && !isSuggested;
  const isAggressive = dtiLevel > DTI_DEFAULT_VALUES.stretched;

  return (
    <Box>
      <Typography variant="h1">{QuizQuestionPresenter.label(question)}</Typography>
      <Box className={classes.controlBar}>
        <Button
          key="suggested"
          onClick={handleClickDtiButton('suggested')}
          className={clsx(classes.button, { [classes.suggested]: isSuggested }, classes.hoverSuggested)}
        >
          {t('components.Quiz.MortgagePaymentQuestion.suggested')}
        </Button>
        <Button
          key="stretched"
          onClick={handleClickDtiButton('stretched')}
          className={clsx(classes.button, { [classes.stretched]: isStretched }, classes.hoverStretched)}
        >
          {t('components.Quiz.MortgagePaymentQuestion.stretched')}
        </Button>
        <Button
          key="aggressive"
          onClick={handleClickDtiButton('aggressive')}
          className={clsx(classes.button, { [classes.aggressive]: isAggressive }, classes.hoverAggressive)}
        >
          {t('components.Quiz.MortgagePaymentQuestion.aggressive')}
        </Button>
      </Box>
      <Box>
        <InputWithSlider
          className={classes.input}
          value={mortgagePayment}
          min={minMortgagePayment}
          max={maxMortgagePayment}
          marksDivider={marksDivider}
          label={QuizQuestionPresenter.inputLabel(question)}
          onChange={handleValueChange}
          strictLimit={strictLimit}
        />
      </Box>
      <Box className={classes.calculationsBar}>
        <Box className={classes.calculationsItem}>
          <Typography variant="body1" color="secondary">
            {t('components.Quiz.MortgagePaymentQuestion.mortgagePayment')}
          </Typography>
          <Typography variant="h1">{QuizPresenter.formattedMoney(mortgagePayment.toFixed(0))}</Typography>
        </Box>
        <Box className={classes.calculationsItem}>
          <Typography variant="body1" color="secondary">
            {t('components.Quiz.MortgagePaymentQuestion.debtToIncome')}
          </Typography>
          <Typography variant="h1">{QuizPresenter.readableDtiLevel(values)}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

MortgagePaymentQuestion.propTypes = {
  question: QuizQuestionPresenter.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  setValidity: PropTypes.func.isRequired,
  values: QuizPresenter.shape().isRequired,
};

export default MortgagePaymentQuestion;



// WEBPACK FOOTER //
// ./src/sharedComponents/Quiz/components/MortgagePaymentQuestion/MortgagePaymentQuestion.js